import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Box } from "@mui/material";
import PageMeta from "../../components/common/PageMeta";
import { useCompanyId } from "../../hooks/useCompanyId";
import { LIMIT, useInquiries } from "./useInquiries";
import InquiryQueue from "./InquiryQueue";
import InquiryDetail from "./InquiryDetail";
import { INK } from "./styles";

const SPLIT_HEIGHT = 'calc(100vh - var(--app-banner-height, 0px) - 72px - 48px)';

export default function PaypalInquiries() {
  const inquiries = useInquiries();
  const { items, page, total, goToNextPage } = inquiries;
  const { disputeId } = useParams();
  const selectedId = disputeId ? Number(disputeId) : null;
  const navigate = useNavigate();
  const { search } = useLocation();
  const { companyId } = useCompanyId();
  const pageCompanyId = useRef(companyId);
  useEffect(() => {
    if (pageCompanyId.current === companyId) return;
    pageCompanyId.current = companyId;
    navigate({ pathname: '/inquiries', search }, { replace: true });
  }, [companyId, navigate, search]);
  const select = useCallback((id: number) => navigate({ pathname: `/inquiries/${id}`, search }), [navigate, search]);
  const index = items.findIndex((item) => item.disputeId === selectedId);
  const openId = useRef(selectedId);
  useEffect(() => { openId.current = selectedId; }, [selectedId]);
  const hasNext = items.length > 0 && (index < 0 || index < items.length - 1 || (page + 1) * LIMIT < total);
  const next = useCallback(async () => {
    if (!hasNext) return;
    if (index < 0) return select(items[0].disputeId);
    if (index < items.length - 1) return select(items[index + 1].disputeId);
    const from = selectedId;
    const first = await goToNextPage();
    if (first && openId.current === from) select(first.disputeId);
  }, [items, index, hasNext, select, goToNextPage, selectedId]);

  return (
    <>
      <PageMeta title="Chargemont | Inquiries" description="Answer PayPal inquiries" />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 2,
          height: { lg: SPLIT_HEIGHT },
          minHeight: { lg: 480 },
          fontSize: 13,
          color: INK.text,
        }}
      >
        <InquiryQueue
          items={inquiries.items}
          counts={inquiries.counts}
          queue={inquiries.queue}
          scope={inquiries.scope}
          onFilters={inquiries.setFilters}
          search={inquiries.search}
          onSearch={inquiries.setSearch}
          selectedId={selectedId}
          onSelect={select}
          page={inquiries.page}
          total={inquiries.total}
          onPage={inquiries.setPage}
          loading={inquiries.loading}
          error={inquiries.error}
        />
        <InquiryDetail id={selectedId} onGone={inquiries.reload} onNext={next} hasNext={hasNext} onChanged={inquiries.reload} />
      </Box>
    </>
  );
}
